import type { AssetGroup } from '@ve-pro/api/asset-group/type'
import { useTrackStore, useProjectStore } from '@ve-pro/store'
import type {
  SegmentCollection,
  SegmentGroupCollection,
  SegmentNoGroupCollection,
  SupportDriftSpeedSegmentCollection1,
} from '@ve-pro/types/track.type'
import { AssetType, AssetGroupType } from '@ve-pro/enums/asset'
import { SegmentType } from '@ve-pro/enums/segment'
import { AsideEventter } from '@ve-pro/ve/aside/config/aside-bus'
import { nextTick } from 'vue'
import { AsideType } from '@ve-pro/ve/aside/config/aside-enum'
import type { ProjectAssetsModel } from '@ve-pro/api/aside/type'
import { hasSegmentMaterial } from '../resolve-segment'

// 获取元件组的最大时长
export function getSegmentGroupDurationMax(
  segment: SegmentGroupCollection,
): number {
  const trackStore = useTrackStore()

  const children = trackStore.segmentGroups[segment.id]

  return Math.max(
    ...(children?.map((child) => child.material?.duration ?? 0) ?? []),
  )
}

// 是否是元件组
export function isSegmentGroup(
  segment: SegmentCollection,
): segment is SegmentGroupCollection {
  return segment.pid === 0 && (segment as any).asset_group_id > 0
}

export function isSegmentGroupChild(
  segment: SegmentCollection,
): segment is SegmentNoGroupCollection {
  return segment.pid > 0 && (segment as any).isGroup === false
}

// 判断是否是可转成组的元件类型
export function isConvertibleToSegmentGroup(
  segment: SegmentCollection,
): segment is SegmentGroupCollection | SegmentNoGroupCollection {
  return (segment as any).hasOwnProperty('isGroup')
}

// 进入组
export function enterGroupMode(activeSegment: SegmentGroupCollection): void {
  if (activeSegment.deprecated) return
  const trackStore = useTrackStore()

  trackStore.activeSegmentGroup = activeSegment
  // 创建成功后，激活左上菜单进入对应的资源组详情
  // 激活左侧资源组一级菜单

  AsideEventter.emit('asideMenuActive', AsideType.AssetGroup)

  // 触发进入资源组详情
  nextTick(() => {
    AsideEventter.emit('enterGroup', activeSegment.asset_group_id)
  })
}

// 退出组模式
export function quitGroup(): void {
  const trackStore = useTrackStore()
  trackStore.activeSegmentGroup = null
}

/**
 * 获取元件组
 * 通过资源组获取元件组
 * @param group 资源组
 * @returns 元件组
 */
export function getSegmentGroup(
  group: AssetGroup,
): SegmentGroupCollection | null {
  const trackStore = useTrackStore()

  return (
    (trackStore.segments.find(
      (segment) =>
        isSegmentGroup(segment) && segment.asset_group_id === group.id,
    ) as SegmentGroupCollection) ?? null
  )
}

/**
 * 获取元件组的默认子元件
 * @param group 元件组
 * @returns 元件组默认子元件
 */
export function getGroupDefaultSegment(
  group: SegmentGroupCollection,
): SegmentNoGroupCollection | null {
  const trackStore = useTrackStore()
  const projectStore = useProjectStore()

  const assetGroup = projectStore.assetGroups.find(
    (t) => t.id === group.asset_group_id,
  )

  const children = trackStore.segmentGroups[group.id]

  // 兼容文本组的情况
  if (assetGroup?.asset_type === AssetGroupType.Text) {
    return (
      children?.find(
        (segment) => segment.res_id === assetGroup?.default_res_id,
      ) ?? null
    )
  }

  return (
    children?.find(
      (segment) => segment.asset_id === assetGroup?.default_asset_id,
    ) ?? null
  )
}

/** 判断是否是组合，获取元件真实的元件数据 */
export function getRealSegment(
  segment: SegmentCollection,
): SegmentCollection | null {
  if (isSegmentGroup(segment)) {
    return getGroupDefaultSegment(segment)
  }

  return segment
}

/**
 * 通过子元件找到其父元件
 * @param segment 子元件
 * @returns 元件组
 */
export function getGroupBySegment(
  segment: SegmentCollection,
): SegmentGroupCollection | null {
  if (segment.pid === 0) return null

  const trackStore = useTrackStore()

  return (
    (trackStore.segments.find(
      (t) => t.id === segment.pid,
    ) as SegmentGroupCollection) ?? null
  )
}

/**
 * 根据资源组和指定资源获取对应轨道上的子元件
 * @param group 资源组
 * @param asset 子资源
 * @returns 子元件 | null
 */
export function getSegmentChildByAsset(
  group: AssetGroup,
  asset: ProjectAssetsModel,
): null | SegmentNoGroupCollection {
  const trackStore = useTrackStore()

  const segmentGroup = getSegmentGroup(group)
  const children =
    (segmentGroup ? trackStore.segmentGroups[segmentGroup.id] : null) ?? []

  return (
    children.find((item) => {
      if (item.type === SegmentType.Text) {
        return item.res_id === asset.id
      }

      return item.asset_id === asset.asset_id
    }) ?? null
  )
}

export function isSupportDriftSpeed(
  segment: SegmentCollection,
): segment is SupportDriftSpeedSegmentCollection1 {
  return (
    !isSegmentGroupChild(segment) &&
    (segment.type === SegmentType.Text || segment.type === SegmentType.Video)
  )
}

// 获取导出总数
export function getGroupExportTotal(): number {
  const trackStore = useTrackStore()

  return trackStore.segments.reduce((total, segment) => {
    const group = trackStore.segmentGroups[segment.id]
    if (group) {
      return total * group.length
    }

    return total
  }, 1)
}

/**
 * 返回剧本中所有非组合的元件
 * @returns SegmentNoGroupCollection[]
 */
export function getNoGroupSegments(): SegmentNoGroupCollection[] {
  const trackStore = useTrackStore()

  return trackStore.segments.reduce((acc, cur) => {
    if (isSegmentGroup(cur)) {
      const group = trackStore.segmentGroups[cur.id]
      if (group) {
        acc.push(...group)
      }
    } else {
      acc.push(cur as SegmentNoGroupCollection)
    }
    return acc
  }, [] as SegmentNoGroupCollection[])
}

/**
 * 获取元件组的子元件
 */
export function getGroupChildSegments(
  group: SegmentGroupCollection,
): SegmentNoGroupCollection[] {
  const trackStore = useTrackStore()

  const children = trackStore.segmentGroups[group.id]

  return children ?? []
}

/**
 * 获取元件转为资产组的类型
 */
export function getAssetGroupTypeBySegment(
  segment: SegmentCollection,
): AssetGroupType {
  const asset_type = hasSegmentMaterial(segment)
    ? segment.material?.type ?? segment.asset_type
    : segment.asset_type
  if (asset_type === AssetType.Text) {
    return AssetGroupType.Text
  }

  if (asset_type === AssetType.Video) {
    return AssetGroupType.Video
  }

  if (asset_type === AssetType.Audio) {
    return AssetGroupType.Audio
  }

  return AssetGroupType.Image
}
